import { type ComponentType, lazy, type LazyExoticComponent } from 'react';

/**
 * Every page except login is its own chunk, loaded when its route is first
 * visited, so the sign-in screen (and the shell) stay small. Keyed by the
 * NAV_ITEMS path: the router renders one route per nav item from this map.
 */
const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K): LazyExoticComponent<ComponentType> =>
  lazy(async () => ({ default: (await load())[name] }));

export const PAGES: Record<string, LazyExoticComponent<ComponentType>> = {
  '/dashboard': named(() => import('@/pages/dashboard'), 'DashboardPage'),
  '/visits': named(() => import('@/pages/visits'), 'VisitsPage'),
  '/assignments': named(() => import('@/pages/assignments'), 'AssignmentsPage'),
  '/anomalies': named(() => import('@/pages/anomalies'), 'AnomaliesPage'),
  '/sync-issues': named(() => import('@/pages/sync-issues'), 'SyncIssuesPage'),
  '/reports/travel': named(() => import('@/pages/travel-report'), 'TravelReportPage'),
  '/reports/activity': named(() => import('@/pages/activity-report'), 'ActivityReportPage'),
  '/tracking/live': named(() => import('@/pages/live-map'), 'LiveMapPage'),
  '/tracking/route': named(() => import('@/pages/route-history'), 'RouteHistoryPage'),
  '/forms': named(() => import('@/pages/form-builder'), 'FormBuilderPage'),
  '/integrations/zoho': named(() => import('@/pages/zoho'), 'ZohoPage'),
  '/catalog/brands': named(() => import('@/pages/brands'), 'BrandsPage'),
  '/catalog/branches': named(() => import('@/pages/branches'), 'BranchesPage'),
  '/catalog/geography': named(() => import('@/pages/geography'), 'GeographyPage'),
  '/catalog/import': named(() => import('@/pages/bulk-import'), 'BulkImportPage'),
  '/catalog/locations': named(() => import('@/pages/locations'), 'LocationsPage'),
  '/catalog/alerts': named(() => import('@/pages/alerts'), 'AlertsPage'),
  '/users': named(() => import('@/pages/users'), 'UsersPage'),
  '/users/devices': named(() => import('@/pages/devices'), 'DevicesPage'),
  '/notifications': named(() => import('@/pages/notifications'), 'NotificationsPage'),
  // Hidden with its nav item (navigation.ts): the app ships through the stores.
  // '/settings': named(() => import('@/pages/settings'), 'SettingsPage'),
};

/** Development-only component gallery (as the previous app's /ui-gallery); `import.meta.env.DEV` drops it from production bundles. */
export const UiGalleryPage: LazyExoticComponent<ComponentType> | null = import.meta.env.DEV
  ? named(() => import('@/pages/ui-gallery'), 'UiGalleryPage')
  : null;
